import { HomeOutlined, SettingOutlined, InfoCircleOutlined } from '@ant-design/icons';
import React, { useMemo } from "react";
import { Space, Row, Col, Card, Table } from "antd";
import 'chart.js/auto';
import 'chartjs-adapter-date-fns';

import { useGetApplicationsQuery } from "../api/api";
import Spinner from "../components/Spinner";
import { Error } from "../components/Error.component";

export const Home = () => {
  const { data, error, isLoading } = useGetApplicationsQuery("");

  const overUsageData = useMemo(() => {
    if (!data) return [];
    return data.data
      .filter(item => {
        const utilization = parseFloat(item.usageCount);
        const purchased = parseFloat(item.quantitypurchasedlicenses_0);
        const percentage = purchased ? Math.round((utilization / purchased) * 100) : 0;
        return percentage > 75;
      })
      .sort((a, b) => {
        const aUtilization = parseFloat(a.usageCount);
        const aPurchased = parseFloat(a.quantitypurchasedlicenses_0);
        const aPercentage = aPurchased ? Math.round((aUtilization / aPurchased) * 100) : 0;
        const bUtilization = parseFloat(b.usageCount);
        const bPurchased = parseFloat(b.quantitypurchasedlicenses_0);
        const bPercentage = bPurchased ? Math.round((bUtilization / bPurchased) * 100) : 0;
        return bPercentage - aPercentage;
      })
      .slice(0, 10); // Change this line to show the last 10 items
  }, [data]);
  

  const totalCostAllApplications = useMemo(() => {
    if (!data) return 0;
    return Math.round(data.data.reduce((total, app) => total + (parseFloat(app.totallicensecostperannum_0) || 0), 0));
  }, [data]);

  const totalNumberOfLicenses = useMemo(() => {
    if (!data) return 0;
    return data.data.length; // Count the number of license items
  }, [data]);

  const earliestEndDateData = useMemo(() => {
    if (!data) return [];
    return [...data.data]
      .filter(item => item.enddateinvoice_0) // Filter out items with empty end dates
      .sort((a, b) => new Date(a.enddateinvoice_0).getTime() - new Date(b.enddateinvoice_0).getTime())
      .slice(0,10)
      .map(item => ({
        ...item,
        enddateinvoice_0: new Date(item.enddateinvoice_0).toLocaleDateString(), // Format date to remove time
      }));
  }, [data]);
  

  const columns = [
    { title: "Name", dataIndex: "licensename_0", key: "licensename_0", className: "table-cell-padding", width: 200 },
    {
      title: 'Publisher',
      dataIndex: 'publishername_0',
      key: 'publishername_0',
      width: 200,
    },
    { title: "Utilization (%)", key: "utilizationPercentage", render: (text, record) => {
        const utilization = parseFloat(record.usageCount);
        const purchased = parseFloat(record.quantitypurchasedlicenses_0);
        const percentage = purchased ? Math.round((utilization / purchased) * 100) : 0;
        let color = "green";
        let fontWeight = "normal";
        if (percentage >= 90) {
          color = "red";
          fontWeight = "bold";
        } else if (percentage >= 80) {
          color = "orange";
        }
        return <span style={{ color, fontWeight }}>{`${percentage}%`}</span>;

        return `${percentage}%`;
      }, className: "table-cell-padding", width: 200 },
  ];

  const endDateColumns = [
    { title: "Name", dataIndex: "licensename_0", key: "licensename_0", className: "table-cell-padding", width: 200 },
    {
      title: 'Publisher',
      dataIndex: 'publishername_0',
      key: 'publishername_0',
      width: 200,
    },
    { title: "License End Date", dataIndex: "enddateinvoice_0", key: "enddateinvoice_0", className: "table-cell-padding", width: 200 },
  ];

  return (
    <div className="full-height flex-container montserrat-font">
      {isLoading && <Spinner />}
      {error && <Error error={"Oops!"} />}
      {data && !error && (
        <Space direction="vertical" size="middle" className="full-width flex-item">
          <Row gutter={16} className="full-width">
            <Col span={12}>
              <Card className="muse-card equal-height-card flex-item">
                <h3>Total cost of all Licenses</h3>
                <h3><p><strong>${totalCostAllApplications.toLocaleString()}</strong></p></h3>
              </Card>
            </Col>
            
            <Col span={12}>
              <Card className="muse-card equal-height-card flex-item">
                <h3>Total License Count (Line Items) </h3>
                <h3><p><strong>{totalNumberOfLicenses.toLocaleString()}</strong></p></h3>
                <h4><em>*Top 20 only</em></h4>
              </Card>
            </Col>
          </Row>
          <Row gutter={24} className="full-width flex-item">
            <Col span={24}>
              <Card className="muse-card flex-item">
                <h3>Overusage Applications</h3>
                <Table
                  dataSource={overUsageData}
                  rowKey="id"
                  columns={columns}
                  className="montserrat-font"
                  rowClassName={() => "table-row-spacing"}
                  pagination={false}
                />
              </Card>
            </Col>
          </Row>
          <Row gutter={24} className="full-width flex-item">
            <Col span={24}>
              <Card className="muse-card flex-item">
                <h3>Licenses nearing end date</h3>
                <Table
                  dataSource={earliestEndDateData}
                  rowKey="id"
                  columns={endDateColumns}
                  className="montserrat-font"
                  rowClassName={() => "table-row-spacing"}
                  pagination={false}
                />
              </Card>
            </Col>
          </Row>
        </Space>
      )}
    </div>
  );
};
